import { useTranslation } from 'react-i18next';

import type { ProfileCard } from '@/types/qriots';
import { BadgeChip } from './BadgeChip';
import { FreshnessLabel } from './FreshnessLabel';

interface TftProfileCardProps {
  data: ProfileCard;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  return `${m}m`;
}

function formatPlayedAt(iso: string): string {
  const diffMs = Date.now() - Date.parse(iso);
  const h = Math.floor(diffMs / 1000 / 3600);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function placementSuffix(p: number): string {
  if (p === 1) return 'st';
  if (p === 2) return 'nd';
  if (p === 3) return 'rd';
  return 'th';
}

export function TftProfileCard({ data }: TftProfileCardProps) {
  const { t } = useTranslation('riot');
  const tft = data.ranks.tft;
  const last = data.lastMatch;

  return (
    <div className="mx-4 flex flex-col gap-4 rounded-xl bg-bg-secondary p-4">
      {/* Header */}
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-fg">
          {data.summonerLevel ? `Level ${data.summonerLevel}` : ''} · {data.region}
        </p>
        <FreshnessLabel freshness={data.freshness} />
      </div>

      {/* TFT rank */}
      <section>
        <p className="text-[10px] font-bold uppercase tracking-wider text-fg-muted">
          {t('title.tftRank')}
        </p>
        {tft ? (
          <div className="mt-1 text-sm text-fg">
            <p className="font-bold">
              {tft.tier} {tft.division} · {tft.lp} LP
            </p>
            <p className="text-fg-muted">
              {tft.wins} W · {tft.losses} L · {(tft.winRate * 100).toFixed(1)}% win
            </p>
          </div>
        ) : (
          <p className="mt-1 text-sm text-fg-muted">{t('title.notRanked')}</p>
        )}
      </section>

      {/* Last TFT match */}
      {last && (
        <section>
          <p className="text-[10px] font-bold uppercase tracking-wider text-fg-muted">
            {t('title.lastMatch')}
          </p>
          <p className="mt-1 text-sm text-fg">
            {last.placement != null ? (
              <>
                <span className={last.win ? 'text-success' : 'text-fg-muted'}>
                  {last.placement}
                  {placementSuffix(last.placement)} {t('title.place')}
                </span>
                {' · '}
              </>
            ) : null}
            {formatDuration(last.gameDuration)} · {formatPlayedAt(last.playedAt)}
          </p>
        </section>
      )}

      {/* Badges */}
      {data.badges && data.badges.length > 0 && (
        <section>
          <p className="text-[10px] font-bold uppercase tracking-wider text-fg-muted">
            {t('title.badges')}
          </p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {data.badges.map((b) => (
              <BadgeChip key={b.code} badge={b} size="sm" />
            ))}
          </div>
        </section>
      )}

      <p className="text-[10px] leading-relaxed text-fg-muted">{data.disclaimer}</p>
    </div>
  );
}
